/**
 * Persist draft small UPS rows created before the parent request is saved (#996 Phase 1).
 * Mirrors rackConfigurationDraft.
 */

import { api } from './api';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { toCreateSmallUpsPayload } from './smallUpsPayload';

import type { SmallUpsConfiguration } from '../types/smallUps';

export async function persistDraftSmallUpsConfigurations(
  requestId: string | number,
  units: SmallUpsConfiguration[],
): Promise<void> {
  const createdIds: number[] = [];
  const idKey = String(requestId);

  try {
    for (const unit of units) {
      const created = await api.post<SmallUpsConfiguration>(
        `/requests/${requestId}/small-ups`,
        toCreateSmallUpsPayload(unit),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
    }
  } catch (error) {
    for (const smallUpsId of createdIds) {
      try {
        await api.delete(`/requests/${idKey}/small-ups/${smallUpsId}`);
      } catch (rollbackError) {
        console.error(
          'Failed to roll back small UPS after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft small UPS after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftSmallUpsConfigurations(
  requestId: string | number,
  units: SmallUpsConfiguration[],
  isOfflineRequest = false,
): Promise<void> {
  if (!units.length) {
    return;
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = units.map((unit, index) => ({
      ...toCreateSmallUpsPayload(unit),
      id: unit.id && unit.id > 0 ? unit.id : -(Date.now() + index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: unit.displayOrder ?? index + 1,
      status: unit.status ?? 'new',
    })) as SmallUpsConfiguration[];

    await offlineCache.updateCachedRequestSmallUps(id, normalized);
    for (const unit of normalized) {
      await offlineCache.addSmallUpsToOfflineQueue(
        id,
        'create',
        toCreateSmallUpsPayload(unit),
        unit.id,
      );
    }
    return;
  }

  await persistDraftSmallUpsConfigurations(requestId, units);
}
